/**
 * Utility functions for report formatting standards
 */

/**
 * Format missing or unavailable values consistently across reports
 * Uses "./." as the standard notation for missing data (matching TREO format)
 */
export const formatMissingValue = (value: any): string => {
  // Check for various "empty" conditions
  if (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "n/a" ||
    value === "N/A" ||
    value === "-" ||
    (typeof value === "string" && value.trim() === "")
  ) {
    return "./."
  }

  // Return the value as string if it exists
  return String(value)
}

/**
 * Format dates in YYYY-MM-DD format
 * Returns "./." if date is invalid or missing
 */
export const formatReportDate = (date: Date | string | null | undefined): string => {
  if (!date) return "./."

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date

    // Check for invalid date
    if (isNaN(dateObj.getTime())) {
      return "./."
    }

    const year = dateObj.getFullYear()
    const month = (dateObj.getMonth() + 1).toString().padStart(2, "0")
    const day = dateObj.getDate().toString().padStart(2, "0")

    return `${year}-${month}-${day}`
  } catch {
    return "./."
  }
}

/**
 * Format dates with time in YYYY-MM-DD HH:MM format
 * Returns "./." if date is invalid or missing
 */
export const formatReportDateTime = (date: Date | string | null | undefined): string => {
  if (!date) return "./."

  try {
    const dateObj = typeof date === "string" ? new Date(date) : date

    // Check for invalid date
    if (isNaN(dateObj.getTime())) {
      return "./."
    }

    const year = dateObj.getFullYear()
    const month = (dateObj.getMonth() + 1).toString().padStart(2, "0")
    const day = dateObj.getDate().toString().padStart(2, "0")
    const hours = dateObj.getHours().toString().padStart(2, "0")
    const minutes = dateObj.getMinutes().toString().padStart(2, "0")

    return `${year}-${month}-${day} ${hours}:${minutes}`
  } catch {
    return "./."
  }
}

/**
 * Format numbers with proper decimal notation
 * Uses period for decimals, optional thousand separators
 */
export const formatReportNumber = (
  value: number | string | null | undefined,
  decimals?: number,
  includeThousandSeparator = false
): string => {
  if (value === null || value === undefined || value === "") {
    return "./."
  }

  const numValue = typeof value === "string" ? parseFloat(value) : value

  if (isNaN(numValue)) {
    return "./."
  }

  let formatted = decimals !== undefined ? numValue.toFixed(decimals) : numValue.toString()

  if (includeThousandSeparator) {
    const parts = formatted.split(".")
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    formatted = parts.join(".")
  }

  return formatted
}

/**
 * Generate test identifier for multi-test reports
 */
export const getTestIdentifier = (index: number, total: number): string => {
  return `Test ${index + 1} of ${total}`
}

/**
 * Format test number for section headers
 */
export const formatTestNumber = (index: number): string => {
  return `Test ${index + 1}`
}

/**
 * Check if a value should be considered empty for reporting
 */
export const isEmptyValue = (value: any): boolean => {
  return (
    value === null ||
    value === undefined ||
    value === "" ||
    value === "n/a" ||
    value === "N/A" ||
    value === "-" ||
    (typeof value === "string" && value.trim() === "") ||
    (Array.isArray(value) && value.length === 0)
  )
}

/**
 * Format a list of items with proper separators
 * Returns "./." if list is empty
 */
export const formatList = (items: any[] | null | undefined, separator = ", "): string => {
  if (!items || !Array.isArray(items) || items.length === 0) {
    return "./."
  }

  const validItems = items.filter((item) => !isEmptyValue(item)).map((item) => String(item))

  return validItems.length > 0 ? validItems.join(separator) : "./."
}
